import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFeedback } from './Feedback';
import { useApi } from './Api';
import { ClaimStateEnum, CompleteClaim, toDateOnly } from './Claims.Context';
import { Email, EmailOptions } from './subscriptions/Subscription.Context';

interface IClaimContext {
  claimData?: CompleteClaim;
  pullData: () => Promise<CompleteClaim>;
  setPending: (note?: string) => Promise<CompleteClaim>;
  setPaid: (note?: string) => Promise<CompleteClaim>;
  setCancelled: (note?: string) => Promise<CompleteClaim>;
  downloadInvoice: () => void;
  downloadCreditNote: () => void;
  createCreditNote: (issued?: Date) => Promise<boolean>;
  deleteClaim: () => Promise<boolean>;
  previewInvoiceEmail: () => Promise<Email>;
  sendInvoiceEmail: (email: EmailOptions) => Promise<CompleteClaim>;
  previewConfirmationEmail: () => Promise<Email>;
  sendConfirmationEmail: (email: EmailOptions) => Promise<CompleteClaim>;
  changeGroup: (groupId: string) => Promise<void>;
}

const ClaimContext = createContext<IClaimContext>({
  claimData: undefined,
  pullData: () => undefined,
  setPending: () => undefined,
  setPaid: () => undefined,
  setCancelled: () => undefined,
  downloadInvoice: () => undefined,
  createCreditNote: () => undefined,
  deleteClaim: () => undefined,
  downloadCreditNote: () => undefined,
  previewInvoiceEmail: () => undefined,
  sendInvoiceEmail: () => undefined,
  previewConfirmationEmail: () => undefined,
  sendConfirmationEmail: () => undefined,
  changeGroup: () => undefined,
});

export const useClaim = () => {
  const context = useContext(ClaimContext);
  if (!context) {
    throw new Error('Parent must be wrapped inside ClaimProvider');
  }
  return context;
};

interface IClaimProvider {
  id: string;
  dataProvider?: boolean;
  initialData?: CompleteClaim;
}

export const ClaimProvider: FC<IClaimProvider> = ({
  id,
  dataProvider = true,
  children,
  initialData,
}) => {
  const { API, defaultErrorHandle } = useApi();
  const { success } = useFeedback();

  const [claimData, setClaimData] = useState<CompleteClaim>(initialData);

  const pullData = useCallback(async (): Promise<CompleteClaim> => {
    if (!API) return undefined;
    console.log('pulling claim');
    try {
      const res = await API.get(`claims/${id}`);
      console.log(res);
      if (dataProvider) setClaimData(res.data);
      return res.data;
    } catch (e: any) {
      defaultErrorHandle(e);
      return undefined;
    }
  }, [API, dataProvider, defaultErrorHandle, id]);

  useEffect(() => {
    if (dataProvider) pullData();
  }, [dataProvider, pullData]);

  const setClaimState = useCallback(
    async (state: ClaimStateEnum, note?: string): Promise<CompleteClaim> => {
      let endPoint: string;
      switch (state) {
        case ClaimStateEnum.pending:
          endPoint = 'setpending';
          break;
        case ClaimStateEnum.paid:
          endPoint = 'setpaid';
          break;
        case ClaimStateEnum.cancelled:
          endPoint = 'setcancelled';
          break;
        default:
          break;
      }
      try {
        const res = await API.post(`claims/${id}/${endPoint}`, { note });
        if (dataProvider) setClaimData(res.data);
        return res.data;
      } catch (e: any) {
        defaultErrorHandle(e);
        return undefined;
      }
    },
    [API, dataProvider, defaultErrorHandle, id]
  );

  const setPending = useCallback(
    async (note?: string) => setClaimState(ClaimStateEnum.pending, note),
    [setClaimState]
  );
  const setPaid = useCallback(
    async (note?: string) => setClaimState(ClaimStateEnum.paid, note),
    [setClaimState]
  );
  const setCancelled = useCallback(
    async (note?: string) => setClaimState(ClaimStateEnum.cancelled, note),
    [setClaimState]
  );

  const downloadInvoice = useCallback(() => {
    API.get(`claims/${id}/invoice`, { responseType: 'blob' }).then((response) => {
      console.log(response);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', claimData.invoice.filename);
      document.body.appendChild(link);
      link.click();
    });
  }, [API, claimData, id]);

  const changeGroup = useCallback(
    async (groupId: string) => {
      const response = await API.post(`claims/${id}/changeGroup`, {
        claimGroups: groupId === '_default' ? [] : [groupId],
      });
      console.log(response);
      if (response.status === 200) {
        setClaimData({
          ...response.data,
        });
        success('Claim group changed successfully');
      }
    },
    [API, id]
  );

  const downloadCreditNote = useCallback(() => {
    if (!claimData.creditNote) return;
    API.get(`claims/${id}/creditNote`, { responseType: 'blob' }).then((response) => {
      console.log(response);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', claimData.creditNote.filename);
      document.body.appendChild(link);
      link.click();
    });
  }, [API, claimData, id]);

  const createCreditNote = useCallback(
    async (issued?: Date): Promise<boolean> => {
      if (!API) return false;
      try {
        await API.post(`claims/${id}/createcreditnote`, {
          issued: issued ? toDateOnly(issued) : undefined,
        });
        success('Dobropis vystavený.');
        pullData();
        return true;
      } catch (e: any) {
        defaultErrorHandle(e);
        return false;
      }
    },
    [API, defaultErrorHandle, id, pullData, success]
  );

  const previewInvoiceEmail = useCallback(async (): Promise<Email> => {
    if (!API) return undefined;
    try {
      const res = await API.get(`claims/${id}/previewInvoiceEmail`);
      console.log(res);
      return res.data;
    } catch (e: any) {
      defaultErrorHandle(e);
      return undefined;
    }
  }, [API, defaultErrorHandle, id]);

  const sendInvoiceEmail = useCallback(
    async (emailOptions: EmailOptions): Promise<CompleteClaim> => {
      if (!API) return undefined;
      try {
        const res = await API.post(`claims/${id}/sendInvoiceEmail`, emailOptions);
        console.log(res);
        if (dataProvider) setClaimData(res.data);
        return res.data;
      } catch (e: any) {
        defaultErrorHandle(e);
        return undefined;
      }
    },
    [API, dataProvider, defaultErrorHandle, id]
  );

  const previewConfirmationEmail = useCallback(async (): Promise<Email> => {
    if (!API) return undefined;
    try {
      const res = await API.get(`claims/${id}/previewConfirmationEmail`);
      console.log(res);
      return res.data;
    } catch (e: any) {
      defaultErrorHandle(e);
      return undefined;
    }
  }, [API, defaultErrorHandle, id]);

  const sendConfirmationEmail = useCallback(
    async (emailOptions: EmailOptions): Promise<CompleteClaim> => {
      if (!API) return undefined;
      try {
        const res = await API.post(`claims/${id}/sendConfirmationEmail`, emailOptions);
        console.log(res);
        if (dataProvider) setClaimData(res.data);
        success('Potvrdenie o platbe odoslané.');
        return res.data;
      } catch (e: any) {
        defaultErrorHandle(e);
        return undefined;
      }
    },
    [API, dataProvider, defaultErrorHandle, id, success]
  );

  const deleteClaim = useCallback(async (): Promise<boolean> => {
    if (!API) return false;
    try {
      const res = await API.delete(`claims/${id}`);
      success('Pohľadávka zmazaná');
      return true;
    } catch (e: any) {
      defaultErrorHandle(e);
      return false;
    }
  }, [API, defaultErrorHandle, id, success]);

  const contextObjects = useMemo(
    () => ({
      claimData,
      pullData,
      setPending,
      setPaid,
      setCancelled,
      downloadInvoice,
      downloadCreditNote,
      createCreditNote,
      previewInvoiceEmail,
      sendInvoiceEmail,
      previewConfirmationEmail,
      sendConfirmationEmail,
      deleteClaim,
      changeGroup,
    }),
    [
      claimData,
      pullData,
      setPending,
      setPaid,
      setCancelled,
      downloadInvoice,
      downloadCreditNote,
      createCreditNote,
      previewInvoiceEmail,
      sendInvoiceEmail,
      previewConfirmationEmail,
      sendConfirmationEmail,
      deleteClaim,
      changeGroup,
    ]
  );

  return <ClaimContext.Provider value={contextObjects}>{children}</ClaimContext.Provider>;
};
